import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
  MapPin,
  Shield,
  Key,
  Save,
  Loader2,
  Camera,
  Lock,
  X,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Clock,
  Activity,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, formatDateTime } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/useToast';
import api from '@/services/api';

interface ProfileData {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  phone?: string;
  profilePhoto?: string;
  role: string;
  jobTitle?: string;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  branch?: {
    id: string;
    name: string;
    code: string;
  };
  department?: {
    id: string;
    name: string;
    code: string;
  };
  is2FAEnabled: boolean;
  createdAt: string;
  lastLogin?: string;
}

interface ActivityItem {
  id: string;
  activityType: string;
  description: string;
  entityType?: string;
  entityName?: string;
  ipAddress?: string;
  createdAt: string;
}

interface UpdateProfileData {
  firstName: string;
  lastName: string;
  phone?: string;
  jobTitle?: string;
  branchId?: string;
  departmentId?: string;
}

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface Branch {
  id: string;
  name: string;
  code: string;
}

interface Department {
  id: string;
  name: string;
  code: string;
}

// Password strength checker
function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score <= 1) return { score, label: 'Weak', color: 'bg-red-500' };
  if (score <= 2) return { score, label: 'Fair', color: 'bg-orange-500' };
  if (score <= 3) return { score, label: 'Good', color: 'bg-yellow-500' };
  if (score <= 4) return { score, label: 'Strong', color: 'bg-green-500' };
  return { score, label: 'Very Strong', color: 'bg-green-600' };
}

// Activity type icon and color mapping
function getActivityIcon(type: string) {
  const iconMap: Record<string, { icon: React.ReactNode; color: string }> = {
    LOGIN: { icon: <User className="w-4 h-4" />, color: 'bg-green-100 text-green-600' },
    LOGOUT: { icon: <User className="w-4 h-4" />, color: 'bg-gray-100 text-gray-600' },
    CREATE: { icon: <CheckCircle2 className="w-4 h-4" />, color: 'bg-blue-100 text-blue-600' },
    UPDATE: { icon: <Activity className="w-4 h-4" />, color: 'bg-yellow-100 text-yellow-600' },
    DELETE: { icon: <XCircle className="w-4 h-4" />, color: 'bg-red-100 text-red-600' },
    VIEW: { icon: <Eye className="w-4 h-4" />, color: 'bg-purple-100 text-purple-600' },
  };
  return iconMap[type] || { icon: <Activity className="w-4 h-4" />, color: 'bg-gray-100 text-gray-600' };
}

export default function ProfilePage() {
  const queryClient = useQueryClient();
  const { user, setUser } = useAuthStore();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<UpdateProfileData>({
    firstName: '',
    lastName: '',
    phone: '',
    jobTitle: '',
    branchId: '',
    departmentId: '',
  });
  
  const [passwordData, setPasswordData] = useState<ChangePasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');

  // Fetch profile data
  const { data: profileData, isLoading, error } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const response = await api.get('/profile');
      return response.data.data as ProfileData;
    },
  });

  // Fetch activity history
  const { data: activityData } = useQuery({
    queryKey: ['profile-activity'],
    queryFn: async () => {
      const response = await api.get('/profile/activity?limit=10');
      return response.data.data as ActivityItem[];
    },
  });

  // Fetch branches
  const { data: branches } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const response = await api.get('/branches');
      return response.data.data as Branch[];
    },
  });

  // Fetch departments
  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await api.get('/departments');
      return response.data.data as Department[];
    },
  });

  // Update form data when profile data loads
  useEffect(() => {
    if (profileData) {
      setFormData({
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || '',
        phone: profileData.phone || '',
        jobTitle: profileData.jobTitle || '',
        branchId: profileData.branch?.id || '',
        departmentId: profileData.department?.id || '',
      });
      setAvatarPreview(profileData.profilePhoto || null);
    }
  }, [profileData]);

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateProfileData) => {
      const response = await api.put('/profile', data);
      return response.data;
    },
    onSuccess: (responseData) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      if (responseData.user) {
        setUser(responseData.user);
      }
      setIsEditing(false);
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been updated successfully.',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Update Failed',
        description: error.response?.data?.error || 'Failed to update profile',
        variant: 'destructive',
      });
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const response = await api.put('/profile/password', data);
      return response.data;
    },
    onSuccess: () => {
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordError('');
      toast({
        title: 'Password Changed',
        description: 'Your password has been changed successfully.',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      setPasswordError(error.response?.data?.error || 'Failed to change password');
    },
  });

  // Upload photo mutation
  const uploadPhotoMutation = useMutation({
    mutationFn: async (photo: string) => {
      const response = await api.put('/profile/photo', { photo });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast({
        title: 'Photo Updated',
        description: 'Your profile photo has been updated.',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.error || 'Failed to upload photo',
        variant: 'destructive',
      });
    },
  });

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (profileData) {
      setFormData({
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || '',
        phone: profileData.phone || '',
        jobTitle: profileData.jobTitle || '',
        branchId: profileData.branch?.id || '',
        departmentId: profileData.department?.id || '',
      });
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Please select an image under 2MB.',
          variant: 'destructive',
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setAvatarPreview(base64);
        uploadPhotoMutation.mutate(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const passwordStrength = getPasswordStrength(passwordData.newPassword);
  const profile = profileData || (user ? { ...user, phone: undefined, createdAt: '', lastLogin: undefined } as ProfileData : null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center text-destructive">
          <p>Failed to load profile. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <User className="w-8 h-8" />
            My Profile
          </h1>
          <p className="text-muted-foreground">
            View and manage your account information
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)}>
              Edit Profile
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button onClick={handleProfileSubmit} disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile Card (Section 1) */}
        <Card className="lg:col-span-1">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <div className="relative">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-3xl font-bold text-primary">
                      {profile?.firstName?.charAt(0)}{profile?.lastName?.charAt(0)}
                    </span>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoSelect}
                />
                <button 
                  className="absolute bottom-0 right-0 p-2 bg-primary rounded-full text-white hover:bg-primary/90 disabled:opacity-50"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadPhotoMutation.isPending}
                >
                  {uploadPhotoMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </button>
              </div>
              <h2 className="mt-4 text-xl font-semibold">
                {profile?.firstName} {profile?.lastName}
              </h2>
              <p className="text-muted-foreground">{profile?.email}</p>
              {profileData?.jobTitle && (
                <p className="text-sm text-muted-foreground">{profileData.jobTitle}</p>
              )}
              <span className={cn(
                'mt-2 px-3 py-1 text-xs font-medium rounded-full',
                'bg-primary/10 text-primary'
              )}>
                <Shield className="w-3 h-3 inline mr-1" />
                {profile?.role}
              </span>
            </div>

            <div className="mt-6 pt-6 border-t space-y-4">
              {profile?.organization && (
                <div className="flex items-center gap-3 text-sm">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span>{profile.organization.name}</span>
                </div>
              )}
              {profile?.branch && (
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{profile.branch.name}</span>
                </div>
              )}
              {profile?.department && (
                <div className="flex items-center gap-3 text-sm">
                  <Briefcase className="w-4 h-4 text-muted-foreground" />
                  <span>{profile.department.name}</span>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowPasswordModal(true)}
              >
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Details Card (Section 1 continued) */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  {isEditing ? (
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                    />
                  ) : (
                    <div className="flex items-center h-10 px-3 bg-muted rounded-md">
                      {profile?.firstName}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  {isEditing ? (
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                    />
                  ) : (
                    <div className="flex items-center h-10 px-3 bg-muted rounded-md">
                      {profile?.lastName}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="flex items-center h-10 px-3 bg-muted rounded-md">
                  <Mail className="w-4 h-4 mr-2 text-muted-foreground" />
                  {profile?.email}
                </div>
                <p className="text-xs text-muted-foreground">Email cannot be changed</p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  {isEditing ? (
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Enter phone number"
                    />
                  ) : (
                    <div className="flex items-center h-10 px-3 bg-muted rounded-md">
                      <Phone className="w-4 h-4 mr-2 text-muted-foreground" />
                      {profileData?.phone || 'Not provided'}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="jobTitle">Job Title / Position</Label>
                  {isEditing ? (
                    <Input
                      id="jobTitle"
                      value={formData.jobTitle}
                      onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                      placeholder="Enter job title"
                    />
                  ) : (
                    <div className="flex items-center h-10 px-3 bg-muted rounded-md">
                      <Briefcase className="w-4 h-4 mr-2 text-muted-foreground" />
                      {profileData?.jobTitle || 'Not provided'}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="branchId">Branch</Label>
                  {isEditing ? (
                    <select
                      id="branchId"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={formData.branchId}
                      onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                    >
                      <option value="">Select branch</option>
                      {branches?.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex items-center h-10 px-3 bg-muted rounded-md">
                      <MapPin className="w-4 h-4 mr-2 text-muted-foreground" />
                      {profile?.branch?.name || 'Not assigned'}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="departmentId">Department</Label>
                  {isEditing ? (
                    <select
                      id="departmentId"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={formData.departmentId}
                      onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                    >
                      <option value="">Select department</option>
                      {departments?.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex items-center h-10 px-3 bg-muted rounded-md">
                      <Briefcase className="w-4 h-4 mr-2 text-muted-foreground" />
                      {profile?.department?.name || 'Not assigned'}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <div className="flex items-center h-10 px-3 bg-muted rounded-md">
                    <Shield className="w-4 h-4 mr-2 text-muted-foreground" />
                    {profile?.role}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Two-Factor Authentication</Label>
                  <div className={cn(
                    'flex items-center h-10 px-3 rounded-md',
                    profileData?.is2FAEnabled 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                  )}>
                    <Lock className="w-4 h-4 mr-2" />
                    {profileData?.is2FAEnabled ? 'Enabled' : 'Not Enabled'}
                  </div>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Activity History Card (Section 3) */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Activity History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activityData && activityData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Action</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Description</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden md:table-cell">IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityData.map((activity) => {
                      const { icon, color } = getActivityIcon(activity.activityType);
                      return (
                        <tr key={activity.id} className="border-b last:border-0 hover:bg-muted/50">
                          <td className="py-3 px-4 text-sm">
                            {formatDateTime(activity.createdAt)}
                          </td>
                          <td className="py-3 px-4">
                            <span className={cn('inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium', color)}>
                              {icon}
                              {activity.activityType}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {activity.description}
                            {activity.entityName && (
                              <span className="text-muted-foreground"> - {activity.entityName}</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground hidden md:table-cell">
                            {activity.ipAddress || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No recent activity</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Change Password Modal (Section 2) */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold">Change Password</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowPasswordModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <form onSubmit={handlePasswordSubmit} className="p-6 space-y-4">
              {passwordError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {passwordError}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordData.newPassword && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={cn('h-full transition-all', passwordStrength.color)}
                          style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium">{passwordStrength.label}</span>
                    </div>
                    <ul className="text-xs text-muted-foreground space-y-0.5">
                      <li className={cn(passwordData.newPassword.length >= 8 ? 'text-green-600' : '')}>
                        {passwordData.newPassword.length >= 8 ? '✓' : '○'} At least 8 characters
                      </li>
                      <li className={cn(/[A-Z]/.test(passwordData.newPassword) && /[a-z]/.test(passwordData.newPassword) ? 'text-green-600' : '')}>
                        {/[A-Z]/.test(passwordData.newPassword) && /[a-z]/.test(passwordData.newPassword) ? '✓' : '○'} Upper & lowercase letters
                      </li>
                      <li className={cn(/\d/.test(passwordData.newPassword) ? 'text-green-600' : '')}>
                        {/\d/.test(passwordData.newPassword) ? '✓' : '○'} At least one number
                      </li>
                      <li className={cn(/[^a-zA-Z0-9]/.test(passwordData.newPassword) ? 'text-green-600' : '')}>
                        {/[^a-zA-Z0-9]/.test(passwordData.newPassword) ? '✓' : '○'} Special character
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                  <p className="text-xs text-destructive">Passwords do not match</p>
                )}
                {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && (
                  <p className="text-xs text-green-600">Passwords match</p>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowPasswordModal(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={changePasswordMutation.isPending || passwordData.newPassword !== passwordData.confirmPassword}
                >
                  {changePasswordMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Change Password
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
